/**
 * Single source of truth for the protocol mappers Prism can create, per client protocol.
 *
 * Both the Create Mapper dialog and the mapper detail panel read from this module, so
 * the labels an admin picks at creation are the labels they see afterwards.
 *
 * `fields` lists the config keys the dialog takes input for, in dialog order.
 * Keys Prism hardcodes are deliberately absent: on SAML `attribute.nameformat` is always
 * 'Basic', and `single` is always 'false' on a role-list mapper. Surfacing a value
 * the admin cannot influence is noise.
 *
 * An OIDC field additionally carries everything needed to render and submit its input —
 * `kind` ('boolean' | 'select' | 'text'), `defaultValue`, `required`, and `options` for a
 * select — so the create dialog renders straight off this table and no view file restates
 * per-key semantics. SAML fields stay {key, label}: that dialog is a hand-written branch
 * per type, and half-populated metadata nothing reads is worse than none.
 *
 * A field may also declare `pinnedTo` and `readOnly`. `pinnedTo` names a value the OWNING
 * APPLICATION supplies (currently only 'clientId') which the backend requires that key to
 * carry — see mapperOwnClientConfigKey in backend/handlers/custom_application_handler.go.
 * Such a key is still sent, because Keycloak expects it; it just is not the admin's to
 * choose, so `readOnly` renders it as a prefilled, non-editable input rather than free text
 * the admin has to guess and then gets rejected for.
 *
 * Protocol values are Keycloak's own: 'saml' and 'openid-connect'. Every helper takes
 * `protocol` as an optional trailing argument and falls back to SAML, so the SAML-only
 * call sites that predate OIDC support keep behaving exactly as they did.
 */

/** Keycloak's protocol discriminator, as the backend canonicalises it. */
export const PROTOCOL_SAML = 'saml';
export const PROTOCOL_OIDC = 'openid-connect';

/**
 * The SAML mapper catalogue. Exported as MAPPER_TYPES (rather than only through
 * MAPPER_TYPES_BY_PROTOCOL) because it is the pre-OIDC public name of this list.
 */
export const MAPPER_TYPES = [
  {
    value: 'user-attribute',
    label: 'User Attribute',
    protocolMapper: 'saml-user-property-mapper',
    helperText: 'Map user properties to SAML attributes',
    fields: [
      { key: 'attribute.name', label: 'SAML attribute name' },
      { key: 'user.attribute', label: 'User attribute' },
      { key: 'friendly.name', label: 'Friendly name' },
    ],
  },
  {
    value: 'role-list',
    label: 'Role List',
    protocolMapper: 'saml-role-list-mapper',
    helperText: 'Map user roles to SAML assertion',
    fields: [
      { key: 'attribute.name', label: 'SAML attribute name' },
      { key: 'friendly.name', label: 'Friendly name' },
    ],
  },
  {
    value: 'group-list',
    label: 'Group List',
    protocolMapper: 'saml-group-membership-mapper',
    helperText: 'Map user group memberships to SAML assertion',
    fields: [
      { key: 'attribute.name', label: 'SAML attribute name' },
      { key: 'friendly.name', label: 'Friendly name' },
      { key: 'full.path', label: 'Full group path' },
      { key: 'single', label: 'Single Group Attribute' },
    ],
  },
];

/** The JSON type Keycloak coerces a claim to. Keycloak's own list, in its own casing. */
const CLAIM_JSON_TYPES = ['String', 'long', 'int', 'boolean', 'JSON'].map((t) => ({
  value: t,
  label: t,
}));

/**
 * The only user properties oidc-usermodel-property-mapper can actually read. Anything
 * else needs the attribute mapper, so this is a closed list rather than free text — the
 * same key is deliberately `kind: 'text'` on the attribute mapper, which is its point.
 */
const BUILT_IN_USER_PROPERTIES = [
  { value: 'username', label: 'Username' },
  { value: 'email', label: 'Email' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
];

/**
 * The OIDC mapper catalogue.
 *
 * Unlike SAML — whose dialog values ('user-attribute', …) predate this module and are
 * kept for compatibility — an OIDC entry's `value` is the Keycloak `protocolMapper` id
 * itself. There is nothing to translate, so nothing to get out of sync.
 *
 * Deliberately four entries. `oidc-full-name-mapper` and friends are not offered:
 * anything Prism cannot create still renders by its raw Keycloak id (see
 * getMapperTypeLabel), which is honest about what an imported client carries.
 *
 * Every `defaultValue` below is Keycloak's own default for that mapper type, so a mapper
 * created here behaves the way the Keycloak console's equivalent would — with one
 * deliberate exception, called out where it happens on the client-role entry.
 */
const OIDC_MAPPER_TYPES = [
  {
    value: 'oidc-usermodel-attribute-mapper',
    label: 'User Attribute',
    protocolMapper: 'oidc-usermodel-attribute-mapper',
    helperText:
      'Map any user attribute — including custom ones — to a token claim. Use this for ' +
      'anything that is not one of the four built-in user properties.',
    fields: [
      { key: 'user.attribute', label: 'User attribute', kind: 'text', defaultValue: '', required: true },
      { key: 'claim.name', label: 'Token claim name', kind: 'text', defaultValue: '', required: true },
      { key: 'jsonType.label', label: 'Claim JSON type', kind: 'select', defaultValue: 'String', options: CLAIM_JSON_TYPES },
      { key: 'multivalued', label: 'Multivalued', kind: 'boolean', defaultValue: false },
      { key: 'id.token.claim', label: 'Add to ID token', kind: 'boolean', defaultValue: true },
      { key: 'access.token.claim', label: 'Add to access token', kind: 'boolean', defaultValue: true },
      { key: 'userinfo.token.claim', label: 'Add to userinfo', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    value: 'oidc-usermodel-property-mapper',
    label: 'User Property',
    protocolMapper: 'oidc-usermodel-property-mapper',
    helperText:
      'Map a built-in user property to a token claim. Reads only username, email, ' +
      'firstName and lastName — for any other attribute use User Attribute, or the ' +
      'claim is silently emitted empty.',
    fields: [
      { key: 'user.attribute', label: 'User property', kind: 'select', defaultValue: '', required: true, options: BUILT_IN_USER_PROPERTIES },
      { key: 'claim.name', label: 'Token claim name', kind: 'text', defaultValue: '', required: true },
      { key: 'jsonType.label', label: 'Claim JSON type', kind: 'select', defaultValue: 'String', options: CLAIM_JSON_TYPES },
      { key: 'id.token.claim', label: 'Add to ID token', kind: 'boolean', defaultValue: true },
      { key: 'access.token.claim', label: 'Add to access token', kind: 'boolean', defaultValue: true },
      { key: 'userinfo.token.claim', label: 'Add to userinfo', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    value: 'oidc-usermodel-client-role-mapper',
    label: 'Client Roles',
    protocolMapper: 'oidc-usermodel-client-role-mapper',
    helperText:
      "Map the user's roles for one client to a token claim. Keycloak adds client roles " +
      'to the ACCESS token only — an app that reads roles from the ID token sees none ' +
      'unless you turn "Add to ID token" on.',
    fields: [
      // Not a choice. The backend pins this key to the application's own clientId and
      // rejects any other value — a mapper naming another client would emit THAT client's
      // roles into this client's token. Left as free text it asked the admin to type a
      // value they were never shown, and answered a wrong guess with a rejection.
      {
        key: 'usermodel.clientRoleMapping.clientId',
        label: 'Client ID',
        kind: 'text',
        defaultValue: '',
        required: true,
        pinnedTo: 'clientId',
        readOnly: true,
        helperText:
          "This application's own client ID. A client-role mapper may only surface the " +
          'roles Prism already manages for this application, so the value is fixed.',
      },
      { key: 'claim.name', label: 'Token claim name', kind: 'text', defaultValue: '', required: true },
      { key: 'jsonType.label', label: 'Claim JSON type', kind: 'select', defaultValue: 'String', options: CLAIM_JSON_TYPES },
      // A user holds a list of client roles, so the claim has to be an array. Keycloak's
      // own built-in client-roles mapper ships multivalued="true" for the same reason:
      // with it off, Keycloak emits a bare string and every consumer reading
      // resource_access.{clientId}.roles as a list breaks.
      { key: 'multivalued', label: 'Multivalued', kind: 'boolean', defaultValue: true },
      // The one deliberate divergence from a Keycloak default in this file. Keycloak
      // leaves client roles out of the ID token, which is exactly the surprise helperText
      // above warns about; defaulting it on here would hide the trap rather than teach it.
      { key: 'id.token.claim', label: 'Add to ID token', kind: 'boolean', defaultValue: false },
      { key: 'access.token.claim', label: 'Add to access token', kind: 'boolean', defaultValue: true },
    ],
  },
  {
    value: 'oidc-group-membership-mapper',
    label: 'Group Membership',
    protocolMapper: 'oidc-group-membership-mapper',
    helperText: 'Map user group memberships to a token claim',
    fields: [
      { key: 'claim.name', label: 'Token claim name', kind: 'text', defaultValue: '', required: true },
      { key: 'full.path', label: 'Full group path', kind: 'boolean', defaultValue: true },
      { key: 'id.token.claim', label: 'Add to ID token', kind: 'boolean', defaultValue: true },
      { key: 'access.token.claim', label: 'Add to access token', kind: 'boolean', defaultValue: true },
      { key: 'userinfo.token.claim', label: 'Add to userinfo', kind: 'boolean', defaultValue: true },
    ],
  },
];

/** Every catalogue, keyed by the protocol value the backend puts on the wire. */
export const MAPPER_TYPES_BY_PROTOCOL = {
  [PROTOCOL_SAML]: MAPPER_TYPES,
  [PROTOCOL_OIDC]: OIDC_MAPPER_TYPES,
};

// 'oidc' never reaches the UI — the backend canonicalises it to 'openid-connect' — but
// it is accepted here so a caller that passes the short form gets OIDC mappers instead
// of silently falling back to the SAML catalogue.
const PROTOCOL_ALIASES = {
  saml: PROTOCOL_SAML,
  'openid-connect': PROTOCOL_OIDC,
  oidc: PROTOCOL_OIDC,
};

/**
 * Anything unrecognised, including nullish, resolves to SAML — the pre-OIDC default.
 *
 * Exported so a caller never has to compare `app.protocol` to PROTOCOL_OIDC by hand: a raw
 * `===` disagrees with this function on the 'oidc' alias, and a view that derives "is this
 * OIDC?" one way and "which mappers?" the other renders half an OIDC form.
 */
export const normalizeProtocol = (protocol) =>
  PROTOCOL_ALIASES[String(protocol || '').toLowerCase()] || PROTOCOL_SAML;

/** The single "is this an OIDC client?" predicate. Alias-aware, unlike `=== PROTOCOL_OIDC`. */
export const isOidcProtocol = (protocol) => normalizeProtocol(protocol) === PROTOCOL_OIDC;

const indexCatalogue = (types) => ({
  byMapperType: Object.fromEntries(types.map((t) => [t.value, t])),
  byProtocolMapper: Object.fromEntries(types.map((t) => [t.protocolMapper, t])),
});

const INDEX_BY_PROTOCOL = {
  [PROTOCOL_SAML]: indexCatalogue(MAPPER_TYPES),
  [PROTOCOL_OIDC]: indexCatalogue(OIDC_MAPPER_TYPES),
};

const indexFor = (protocol) => INDEX_BY_PROTOCOL[normalizeProtocol(protocol)];

/** The mapper types the create dialog offers for a protocol. Defaults to SAML. */
export const getMapperTypesForProtocol = (protocol) =>
  MAPPER_TYPES_BY_PROTOCOL[normalizeProtocol(protocol)];

// Keycloak stores every config value as a string.
const BOOLEAN_VALUES = { true: true, false: false };

/** `some.custom.key` -> `Some custom key` */
const humanizeKey = (key) => {
  const words = key.replace(/[._-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Booleans arrive as the strings 'true'/'false'; everything else stays text. */
const toRow = (label, rawValue) =>
  rawValue in BOOLEAN_VALUES
    ? { label, value: BOOLEAN_VALUES[rawValue], kind: 'boolean' }
    : { label, value: rawValue, kind: 'text' };

/** The Keycloak `protocolMapper` a dialog mapper-type value maps to. */
export const getProtocolMapper = (mapperType, protocol) =>
  indexFor(protocol).byMapperType[mapperType]?.protocolMapper;

/** The helper text shown under the dialog's Mapper Type select. */
export const getMapperTypeHelperText = (mapperType, protocol) =>
  indexFor(protocol).byMapperType[mapperType]?.helperText || '';

/**
 * The fields a mapper type's create-dialog form is built from, in dialog order.
 *
 * Each entry is the whole contract for one input — see the module docblock. An unknown
 * type yields an empty list, which renders as a form with no type-specific inputs rather
 * than throwing.
 */
export const getMapperFields = (mapperType, protocol) =>
  indexFor(protocol).byMapperType[mapperType]?.fields || [];

/**
 * The seed config for a freshly selected mapper type: every field at its catalogue
 * default. Re-read on every type change, so no value carries across from the type the
 * admin passed through.
 *
 * `pins` supplies the values for fields that declare `pinnedTo` — `{ clientId }` from the
 * application being edited. A pinned field takes its value from here instead of its
 * catalogue default, which is what makes the read-only Client ID input arrive prefilled
 * rather than empty-and-required. Omitting `pins` leaves every field at its default, so
 * the SAML-era two-argument call sites are unaffected.
 */
export const getMapperFieldDefaults = (mapperType, protocol, pins = {}) =>
  Object.fromEntries(
    getMapperFields(mapperType, protocol).map(({ key, defaultValue, pinnedTo }) => [
      key,
      pinnedTo && pins[pinnedTo] != null ? pins[pinnedTo] : defaultValue,
    ])
  );

/**
 * Display name for a mapper's `protocolMapper`, using the label the create dialog
 * shows. A type Prism cannot create returns the raw string, so it still identifies
 * itself rather than rendering blank.
 */
export const getMapperTypeLabel = (protocolMapper, protocol) => {
  if (!protocolMapper) return '';
  return indexFor(protocol).byProtocolMapper[protocolMapper]?.label || protocolMapper;
};

/**
 * Flatten a mapper into ordered display rows, limited to what Prism lets an admin
 * configure: the mapper type and name, then the create dialog's fields for that type.
 *
 * `protocol` is optional: a Keycloak mapper carries its own `protocol` field, so an
 * explicit argument only has to be passed when the caller holds a mapper that does not.
 *
 * @returns {Array<{label: string, value: *, kind: 'text'|'boolean'}>}
 */
export const getMapperDetailRows = (mapper, protocol) => {
  if (!mapper) return [];

  const index = indexFor(protocol || mapper.protocol);
  const rows = [];

  if (mapper.protocolMapper) {
    const label = index.byProtocolMapper[mapper.protocolMapper]?.label || mapper.protocolMapper;
    rows.push({ label: 'Mapper type', value: label, kind: 'text' });
  }
  if (mapper.name) rows.push({ label: 'Name', value: mapper.name, kind: 'text' });

  const config = mapper.config || {};
  const settableFields = index.byProtocolMapper[mapper.protocolMapper]?.fields;

  if (settableFields) {
    settableFields.forEach(({ key, label }) => {
      if (key in config) rows.push(toRow(label, config[key]));
    });
    return rows;
  }

  // A mapper type Prism cannot create — an imported client can carry one. There is no
  // dialog to mirror, so show the whole config rather than hide how the mapper behaves.
  Object.keys(config)
    .sort()
    .forEach((key) => rows.push(toRow(humanizeKey(key), config[key])));

  return rows;
};
